/**
 * Authentication Context Provider
 * Provides shared authentication state across all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config/config';
import cognitoService from '../services/cognito';
import apiClient from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check for existing authentication session
   */
  const checkAuthState = useCallback(async () => {
    setIsLoading(true);

    try {
      if (!config.features.authEnabled) {
        // Development mode: auto-authenticate with dev user
        // Try to load actual user data from the API
        try {
          const profile = await apiClient.getInterests();
          setUser({
            id: config.devUserId,
            name: profile.name || 'Development User',
            email: profile.email || 'dev@vocabmaster.local',
            nativeLanguage: profile.nativeLanguage || 'en',
          });
        } catch (error) {
          // If profile doesn't exist yet, use defaults
          setUser({
            id: config.devUserId,
            name: 'Development User',
            email: 'dev@vocabmaster.local',
            nativeLanguage: 'en',
          });
        }
        setIsAuthenticated(true);
      } else {
        // Production mode: check for Cognito session
        const attributes = await cognitoService.getUserAttributes();

        if (attributes && attributes.sub) {
          // Prefer DynamoDB name (editable by user) over Cognito name
          let displayName = attributes.name;
          try {
            const profile = await apiClient.getInterests();
            if (profile.name) displayName = profile.name;
          } catch {
            // Fall back to Cognito name if profile fetch fails
          }

          setUser({
            id: attributes.sub,
            name: displayName,
            email: attributes.email,
            emailVerified: attributes.emailVerified,
          });
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    } catch (error) {
      // No valid session found
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check auth state on mount
  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  /**
   * Sign up a new user
   */
  const signup = useCallback(async (email, password, name) => {
    try {
      const result = await cognitoService.signUp(email, password, name);
      return result;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }, []);

  /**
   * Confirm email verification code
   */
  const confirmSignup = useCallback(async (email, code) => {
    try {
      const result = await cognitoService.confirmSignUp(email, code);
      return result;
    } catch (error) {
      console.error('Confirm signup error:', error);
      throw error;
    }
  }, []);

  /**
   * Login with email/password
   */
  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    try {
      const result = await cognitoService.signIn(email, password);

      if (result.isSignedIn) {
        // Get user attributes and update state
        const attributes = await cognitoService.getUserAttributes();

        // Prefer DynamoDB name (editable by user) over Cognito name
        let displayName = attributes.name;
        try {
          const profile = await apiClient.getInterests();
          if (profile.name) displayName = profile.name;
        } catch {
          // Fall back to Cognito name if profile fetch fails
        }

        setUser({
          id: attributes.sub,
          name: displayName,
          email: attributes.email,
          emailVerified: attributes.emailVerified,
        });
        setIsAuthenticated(true);
      }

      return result;
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login with Google OAuth via Cognito federated sign-in
   * This triggers a redirect to Cognito hosted UI -> Google -> back to app
   * After redirect, checkAuthState will pick up the authenticated user
   */
  const signInWithGoogle = useCallback(async () => {
    try {
      if (!config.features.authEnabled) {
        throw new Error('Authentication is not enabled');
      }

      // This will redirect the page - code after this won't execute
      await cognitoService.signInWithGoogle();
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      if (config.features.authEnabled) {
        await cognitoService.signOut();
      }

      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }, []);

  /**
   * Refresh authentication token
   */
  const refreshToken = useCallback(async () => {
    try {
      if (!config.features.authEnabled) {
        return;
      }

      // Cognito handles token refresh automatically
      // Just verify the session is still valid
      await cognitoService.getSession();
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout user
      await logout();
      throw error;
    }
  }, [logout]);

  const updateUser = useCallback((fields) => {
    setUser((prev) => prev ? { ...prev, ...fields } : prev);
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    signup,
    confirmSignup,
    login,
    signInWithGoogle,
    logout,
    refreshToken,
    checkAuthState,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
